import {Trans} from '@common/i18n/trans';
import {Link} from '@app/dashboard/links/link';
import {useSettings} from '@common/core/settings/use-settings';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {ShareLinkButton} from '@app/dashboard/links/sharing/share-link-button';
import {Footer} from '@common/ui/footer/footer';
import {RedirectCountdownButton} from '@app/short-links/renderers/redirect-countdown-button';
import {Button} from '@common/ui/buttons/button';
import {AdHost} from '@common/admin/ads/ad-host';
import {Link as RouterLink} from 'react-router-dom';
import {useEffect, useMemo, useState} from 'react';

type LinkWithVisitConfig = Link & {
  visitConfig?: {adsEnabled?: boolean; tier?: string; reason?: string};
  visit_config?: {
    adsEnabled?: boolean;
    ads_enabled?: boolean;
    tier?: string;
    reason?: string;
  };
};

interface LinkPageRendererProps {
  link: Link;
}

function PreviewSpinner() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-white/70">
      <div
        className="h-28 w-28 animate-spin rounded-full border-4 border-primary border-t-transparent"
        role="status"
        aria-label="Loading preview"
      />
    </div>
  );
}

export function LinkSplashRenderer({link}: LinkPageRendererProps) {
  const {base_url} = useSettings();
  const l = link as LinkWithVisitConfig;

  const adsEnabledFromBackend =
    l.visitConfig?.adsEnabled ??
    l.visit_config?.adsEnabled ??
    l.visit_config?.ads_enabled ??
    undefined;

  const adsEnabled =
    typeof adsEnabledFromBackend === 'boolean' ? adsEnabledFromBackend : true;

  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoading, setImgLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const previewSrc = useMemo(() => {
    const ts = Date.now() + attempt;
    return `${base_url}/${link.hash}/img?ts=${ts}`;
  }, [base_url, link.hash, attempt]);

  useEffect(() => {
    setImgLoading(true);
  }, [previewSrc]);

  useEffect(() => {
    if (!imgFailed) return;
    const t = setTimeout(() => {
      setImgFailed(false);
      setAttempt(a => a + 1);
    }, 900);
    return () => clearTimeout(t);
  }, [imgFailed]);

  return (
    <div className="flex flex-col w-full h-screen bg-alt">
      <Navbar
        menuPosition="link-page-navbar"
        rightChildren={<ShareLinkButton link={link} />}
      />

      <div className="container flex-auto flex flex-col items-center justify-center mx-auto px-24">
        {adsEnabled ? (
          <AdHost slot="splash_top" className="mt-20 mb-60 flex-shrink-0" />
        ) : null}

        <div className="border rounded md:flex gap-24 p-20 bg-paper flex-shrink-0">
          {/* Preview box */}
          <div className="flex-shrink-0 border w-320 h-240 rounded max-w-full relative overflow-hidden bg-gray-50">
            {(imgLoading || imgFailed) ? <PreviewSpinner /> : null}

            <img
              src={previewSrc}
              alt=""
              className="w-full h-full object-contain"
              onLoad={() => {
                setImgLoading(false);
                setImgFailed(false);
              }}
              onError={() => {
                setImgLoading(false);
                setImgFailed(true);
              }}
              loading="eager"
            />
          </div>

          <div>
            <h1 className="text-2xl mt-24 md:mt-0 mb-24">
              <Trans message="You are about to be redirected to another page." />
            </h1>
            <div>
              <RedirectCountdownButton variant="flat" color="primary" link={link} />
              <Button className="ml-10" elementType={RouterLink} to="/">
                <Trans message="Go back" />
              </Button>
            </div>
            <div className="text-sm text-muted border-t mt-24 pt-24">
              <Trans message="You are about to be redirected to another page. We are not responsible for the content of that page or the consequences it may have on you." />
            </div>
          </div>
        </div>

        {adsEnabled ? (
          <AdHost slot="splash_bottom" className="mt-60 mb-20 flex-shrink-0" />
        ) : null}
      </div>

      <Footer className="px-24" />
    </div>
  );
}
